import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast, ToastOptions } from 'react-toastify'

import {
  RelacaoDocumentosLicencaAluno,
  UploadDocumentos,
  UploadDocumentosCertificacao
} from 'src/compartilhados'
import { ResumoDeDocumentosMultiplosDeEntradaParceiroCertificacaoMapper } from 'src/compartilhados/modulos/gestao-de-documentos/mappers/resumo-de-documentos.mapper'
import {
  Carregando,
  ErroContainerLayout,
  ErroLayoutContainer,
  Modal,
  ModalRef
} from 'src/componentes'
import { RotasParceiroCertificacao } from 'src/rotas'
import { Api } from 'src/servicos'
import { Documento } from 'src/tipos'

import { ContainerDaPagina } from './style'

const erroAoBaixarDocumentos = 'Erro ao baixar documentos.'
const ErroAoObterDocumentos =
  'Não foi possível obter os documentos do parceiro.'

const toastOptions = { type: 'error' } as ToastOptions

export type DocumentosParceiroCertificacaoHandles = {
  enviarDocumentos: () => void
}

interface DocumentosParceiroCertificacaoProps {
  parceiroCertificacaoId: string | undefined // ou substitua `any` pelo tipo apropriado
  documentos: UploadDocumentos[]
  atualizar: (novosDocumentos: UploadDocumentos) => void
  reiniciar: (novosDocumentos: UploadDocumentos[]) => void
}

const DocumentosParceiroCertificacao = forwardRef<
  DocumentosParceiroCertificacaoHandles,
  DocumentosParceiroCertificacaoProps
>(({ parceiroCertificacaoId, documentos, atualizar, reiniciar }) => {
  const history = useHistory()
  const [pronto, definirPronto] = useState<boolean>()
  const modalDeCancelamento = useRef<ModalRef>(null)

  const { limparErro, definirErro, erro } = ErroLayoutContainer.useContainer()

  const tiposLicenca = useMemo(
    () => RelacaoDocumentosLicencaAluno.map(x => x.tipo),
    [RelacaoDocumentosLicencaAluno]
  )

  const obterResumoDeDocumentos = async (idDoUsuario: string) => {
    try {
      definirPronto(false)
      limparErro()
      const resposta = await Api.ObterResumoDeDocumentosParceiroCertificacao(
        idDoUsuario
      )

      const resultado = ResumoDeDocumentosMultiplosDeEntradaParceiroCertificacaoMapper(
        documentos,
        resposta
      )

      reiniciar(resultado)
    } catch (erro) {
      const mensagens = ErroAoObterDocumentos
      const acaoVoltar = () => history.goBack()
      definirErro({ mensagens, acaoVoltar })
    } finally {
      definirPronto(true)
    }
  }

  const abrirImagemNovaAba = (documentoParceiro: Documento) => {
    const novaAba = window.open('')
    if (documentoParceiro.contentType === 'application/pdf') {
      novaAba.document.write(
        `<iframe width='100%' height='100%' src='data:${documentoParceiro?.contentType};base64, ` +
          encodeURI(documentoParceiro?.base64) +
          "' />"
      )
    } else {
      const img = `<img src='${`data:${documentoParceiro?.contentType};base64, ${documentoParceiro?.base64}`}' />`
      novaAba.document.write(img)
    }
    novaAba.document.title = 'Documento do parceiro'
  }

  const visualizarDocumento = async (documentoId: string) => {
    try {
      const novoDocumento = await Api.RequisitarDocumentoParceiroCertificacao(
        parceiroCertificacaoId,
        documentoId
      )
      if (novoDocumento) {
        abrirImagemNovaAba(novoDocumento)
      }
    } catch (error) {
      toast(ErroAoObterDocumentos, toastOptions)
    }
  }

  const excluirDocumento = async (documentoId: string) => {
    try {
      await Api.RemoverDocumentoParceiroCertificacao(
        parceiroCertificacaoId,
        documentoId
      )
      reiniciar(documentos.filter(doc => doc.id !== documentoId))
    } catch (error) {
      toast(ErroAoObterDocumentos, toastOptions)
    }
  }

  const obterDownloadDeDocumentos = async (
    documentoId: string
  ): Promise<void> => {
    try {
      await Api.DownloadDocumentosParceiroCertificacao(
        parceiroCertificacaoId,
        documentoId
      )
    } catch (erro) {
      toast(erroAoBaixarDocumentos, toastOptions)
    }
  }

  const documentosParceiro = useMemo(() => {
    return documentos.filter(x => !tiposLicenca.some(y => y === x.tipo))
  }, [documentos])

  useEffect(() => {
    if (parceiroCertificacaoId) {
      obterResumoDeDocumentos(parceiroCertificacaoId)
    }
    definirPronto(true)
  }, [])

  return pronto && !erro ? (
    <ContainerDaPagina>
      {/* <TituloComplementar>Envio de PPC</TituloComplementar>
      <Dicas>O envio de PPC é obrigatório para abertura de turma</Dicas> */}
      {documentosParceiro.map(documento => (
        <UploadDocumentosCertificacao
          key={documento.id}
          documento={documento}
          eventoDeMudanca={atualizar}
          eventoDeDownload={obterDownloadDeDocumentos}
          eventoDeVisualizacao={visualizarDocumento}
          eventoDeExclusao={excluirDocumento}
        />
      ))}
      <Modal
        ref={modalDeCancelamento}
        backdrop
        id="modal-de-cancelamento"
        titulo={'Deseja cancelar?'}
        acaoPrimaria={{
          titulo: 'Sim',
          tipo: 'button',
          acao: () => history.push(RotasParceiroCertificacao.Dashboard)
        }}
        acaoSecundario={{
          titulo: 'Não',
          tipo: 'button',
          acao: () => modalDeCancelamento?.current?.fechar()
        }}
      >
        <p>Selecione uma opção</p>
      </Modal>
    </ContainerDaPagina>
  ) : !pronto && !erro ? (
    <Carregando texto="Carregando documentos do parceiro..." />
  ) : (
    <ErroContainerLayout />
  )
})

export default DocumentosParceiroCertificacao
